import React, { useState, useEffect, useCallback } from "react";
import { endpoints } from "../api";

const Tracker = () => {
  const [activeTab, setActiveTab] = useState("performance");
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);

  const userEmail = localStorage.getItem("user_email");
  const campaignName = localStorage.getItem("active_campaign");

  const [editingSubjectIdx, setEditingSubjectIdx] = useState(null);
  const [editingBodyIdx, setEditingBodyIdx] = useState(null);





  const fetchPerformance = useCallback(async () => {
    try {
      setLoading(true);
      const res = await endpoints.getPerformance(userEmail, campaignName);
      setPerfData(res.data);
    } catch (e) {
      console.error("Error fetching performance", e);
    } finally {
      setLoading(false);
    }
  }, [userEmail, campaignName]);

    const fetchCampaignDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await endpoints.get_campaign_details(userEmail, campaignName);
      // This populates perfData with the full object including goal, tone, etc.
      setPerfData(res.data);
    } catch (e) {
      console.error("Error fetching campaign details", e);
    } finally {
      setLoading(false);
    }
  }, [userEmail, campaignName]);

    useEffect(() => {
    // Clear data so the table doesn't try to map "Edit Campaign" data
    setPerfData(null);

    if (activeTab === "performance") {
      fetchPerformance();
    } else if (
      activeTab === "emails" ||
      activeTab === "edit_campaign" ||
      activeTab === "schedule"
    ) {
      fetchCampaignDetails();
    }
  }, [activeTab, fetchPerformance, fetchCampaignDetails]);

  // Add optional chaining (?.) and fallbacks to prevent the crash
  const totalSent = perfData?.stats
    ? perfData.stats.filter((s) => s.sent).length
    : 0;
  const totalOpens = perfData?.stats
    ? perfData.stats.reduce((acc, curr) => acc + (curr.open_count || 0), 0)
    : 0;

  // Handler for updating campaign settings
  const handleUpdateCampaign = async () => {
    try {
      const payload = {
        user_email: userEmail,
        campaign_name: campaignName,
        industry: perfData.industry, // Pulled from the get_campaign_details response
        tone: perfData.tone,
        goal: perfData.goal,
        follow_up_count: perfData.follow_up_count,
        prompt: "Offer a premium invitation to our winter collection.",
      };

      const res = await endpoints.edit_campaign(payload);
      if (res.status === 200) {
        alert("Settings updated successfully!");
        // Refresh local data to show new values
        fetchCampaignDetails();
      }
    } catch (e) {
      console.error("Update failed", e);
      alert("Could not update campaign settings.");
    }
  };

  const handleSaveEmailSelection = async () => {
    try {
      const selectedIdxS = perfData.selection_metadata.selected_subject_index;
      const selectedIdxB = perfData.selection_metadata.selected_body_index;

      const payload = {
        user_email: userEmail,
        campaign_name: campaignName,
        subject_index: selectedIdxS,
        body_index: selectedIdxB,
        // Sending the actual text so the backend can update the DB strings
        subject: perfData.ai_generated_content.subject_lines[selectedIdxS],
        body: perfData.ai_generated_content.email_bodies[selectedIdxB],
      };

      const res = await endpoints.editEmail(payload);
      if (res.status === 200) {
        alert("Email version and content updated successfully!");
        fetchCampaignDetails();
      }
    } catch (e) {
      alert("Failed to update email selection.");
    }
  };

  const handleCSVUpload = async () => {
    if (!perfData?.selectedFile) return;

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("user_email", userEmail);
      fd.append("campaign_name", campaignName);
      fd.append("file", perfData.selectedFile);

      const res = await endpoints.editCSV(fd);
      if (res.status === 200) {
        alert("Contact list updated successfully!");
        // Clear the file selection
        setPerfData({ ...perfData, selectedFile: null });
        // Refresh performance data to show the new recipient list
        fetchPerformance();
        setActiveTab("performance");
      }
    } catch (e) {
      console.error("CSV Upload failed", e);
      alert("Failed to upload CSV. Please ensure the format is correct.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      setLoading(true);
      const payload = {
        user_email: userEmail,
        campaign_name: campaignName,
        schedule_type: perfData?.temp_schedule_type || "now",
        scheduled_date:
          perfData?.temp_schedule_type === "scheduled"
            ? perfData?.temp_schedule_date
            : "", // Or null, depending on your backend requirement
      };

      const res = await endpoints.edit_schedule(payload);
      if (res.status === 200) {
        alert("Schedule updated successfully!");
        fetchCampaignDetails(); // Refresh campaign state
        setActiveTab("performance"); // Take them back to overview
      }
    } catch (e) {
      console.error("Schedule update failed", e);
      alert("Failed to update schedule. Please check the date format.");
    } finally {
      setLoading(false);
    }
  };

  // Re-use your existing selectStyle
  const selectStyle = {
    width: "100%",
    padding: "12px",
    background: "var(--input)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    color: "white",
    marginTop: "8px",
  };

  return (
    <div style={{ color: "white" }}>
      {/* Header Area */}
      <div style={{ marginBottom: "30px" }}>
        <h2 style={{ margin: 0, fontSize: "28px" }}>{campaignName}</h2>
        <p style={{ color: "var(--text-dim)" }}>
          Campaign Management & Analytics
        </p>
      </div>

      {/* TAB NAVIGATION */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          borderBottom: "1px solid var(--border)",
          marginBottom: "30px",
        }}
      >
        {["performance", "edit_campaign", "emails", "csv", "schedule"].map(
          (tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: "12px 24px",
                background: "none",
                border: "none",
                borderBottom:
                  activeTab === tab
                    ? "2px solid var(--accent)"
                    : "2px solid transparent",
                color: activeTab === tab ? "var(--accent)" : "var(--text-dim)",
                cursor: "pointer",
                fontWeight: "bold",
                textTransform: "capitalize",
                transition: "0.3s",
              }}
            >
              {tab === "edit_campaign"
                ? "Edit Campaign"
                : tab === "emails"
                ? "Edit Emails"
                : tab.toUpperCase()}
            </button>
          )
        )}
      </div>

      {/* PERFORMANCE CONTENT */}
      {activeTab === "performance" && (
        <div
          style={{ width: "100%", display: "flex", flexDirection: "column" }}
        >
          {loading ? (
            <p style={{ color: "var(--text-dim)", textAlign: "center" }}>
              Loading...
            </p>
          ) : perfData ? (
            <>
              {/* 1. TOP STAT CARDS - Using the new CSS class */}
              {/* TOP STAT CARDS - Force-Separation Fix */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "20px",
                  marginBottom: "40px",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "stretch",
                }}
              >
                <div style={{ flex: 1 }}>
                  <StatCard
                    label="Total Contacts"
                    value={perfData.total_contacts}
                    icon="👥"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <StatCard label="Emails Sent" value={totalSent} icon="📩" />
                </div>
                <div style={{ flex: 1 }}>
                  <StatCard label="Total Opens" value={totalOpens} icon="👁️" />
                </div>
              </div>

              {/* 2. RECIPIENT TABLE - Using the new CSS class */}
              <div className="performance-table-container">
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    textAlign: "left",
                  }}
                >
                  <thead
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      color: "var(--accent)",
                    }}
                  >
                    <tr>
                      <th style={{ padding: "20px" }}>Recipient Email</th>
                      <th style={{ padding: "20px" }}>Status</th>
                      <th style={{ padding: "20px" }}>Opens</th>
                      <th style={{ padding: "20px" }}>Last Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perfData?.stats?.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{ borderBottom: "1px solid var(--border)" }}
                      >
                        <td style={{ padding: "15px 20px" }}>{row.email}</td>
                        <td style={{ padding: "15px 20px" }}>
                          <span
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              background: row.sent
                                ? "rgba(34, 197, 94, 0.1)"
                                : "rgba(255, 255, 255, 0.05)",
                              color: row.sent ? "#4ade80" : "var(--text-dim)",
                              border: row.sent
                                ? "1px solid #4ade80"
                                : "1px solid var(--border)",
                            }}
                          >
                            {row.sent ? "SENT" : "PENDING"}
                          </span>
                        </td>
                        <td style={{ padding: "15px 20px" }}>
                          {row.open_count}
                        </td>
                        <td
                          style={{
                            padding: "15px 20px",
                            color: "var(--text-dim)",
                          }}
                        >
                          {row.last_opened}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* EDIT CAMPAIGN TAB */}
      {activeTab === "edit_campaign" && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            padding: "20px 0",
          }}
        >
          <div
            className="auth-card"
            style={{
              width: "100%",
              maxWidth: "500px",
              padding: "30px",
              margin: "0",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "25px", color: "white" }}>
              Edit Campaign Settings
            </h3>

            {loading ? (
              <p style={{ textAlign: "center", color: "var(--text-dim)" }}>
                Loading settings...
              </p>
            ) : (
              <div className="input-group">
                <label style={{ color: "var(--text-dim)", fontSize: "13px" }}>
                  Campaign Goal
                </label>
                <input
                  type="text"
                  placeholder="e.g. abandoned carts, welcome series, re-engagement..."
                  value={perfData?.goal || ""}
                  style={{ marginBottom: "20px" }}
                  onChange={(e) => setPerfData({ ...perfData, goal: e.target.value })}
                />

                <div
                  style={{ display: "flex", gap: "20px", marginBottom: "20px" }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{ color: "var(--text-dim)", fontSize: "13px" }}
                    >
                      Tone
                    </label>
                    <select
                      value={perfData?.tone || ""}
                      style={miniSelectStyle}
                      onChange={(e) =>
                        setPerfData({ ...perfData, tone: e.target.value })
                      }
                    >
                      {[
                        "Witty",
                        "Formal",
                        "Informal",
                        "Engaging",
                        "Data-Based",
                        "Authoritative",
                        "Friendly-Professional",
                      ].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: 1 }}>
                    <label
                      style={{ color: "var(--text-dim)", fontSize: "13px" }}
                    >
                      Follow-ups
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={perfData?.follow_up_count || ""}
                      style={{ marginTop: "8px" }}
                      onChange={(e) =>
                        setPerfData({
                          ...perfData,
                          follow_up_count: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <button className="btn-primary" onClick={handleUpdateCampaign}>
                  Update Campaign Details
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EMAIL EDITING TAB */}
      {activeTab === "emails" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "30px" }}>
          {loading ? (
            <p style={{ textAlign: "center", color: "var(--text-dim)" }}>
              Loading email content...
            </p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "30px",
                }}
              >
                {/* SUBJECTS SECTION */}
                <div>
                  <h4 style={{ marginBottom: "15px", color: "var(--accent)" }}>
                    Select & Edit Subject Line
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {perfData?.ai_generated_content?.subject_lines.map(
                      (line, idx) => {
                        const isSelected =
                          perfData?.final_subject?.text === line;
                        const isActive =
                          perfData?.selection_metadata
                            ?.selected_subject_index === idx;

                        return (
                          <div
                            key={idx}
                            onClick={() =>
                              setPerfData({
                                ...perfData,
                                selection_metadata: {
                                  ...perfData.selection_metadata,
                                  selected_subject_index: idx,
                                },
                              })
                            }
                            style={{
                              padding: "15px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: "2px solid",
                              transition: "0.2s",
                              position: "relative",
                              borderColor: isActive
                                ? "#f97316"
                                : isSelected
                                ? "var(--accent)"
                                : "var(--border)",
                              background: isActive
                                ? "rgba(249,115,22,0.15)"
                                : "var(--card)",
                            }}
                          >
                            {/* BADGES */}
                            {isSelected && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "40px",
                                  background: "var(--accent)",
                                  color: "black",
                                  fontSize: "10px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontWeight: "bold",
                                }}
                              >
                                ACTIVE
                              </span>
                            )}
                            {isActive && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "40px",
                                  background: "#f97316",
                                  color: "black",
                                  fontSize: "10px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontWeight: "bold",
                                }}
                              >
                                NEW SELECTED
                              </span>
                            )}

                            {/* EDITABLE INPUT */}
                            {editingSubjectIdx === idx ? (
                              <input
                                autoFocus
                                style={{
                                  width: "90%",
                                  background: "transparent",
                                  color: "white",
                                  border: "none",
                                  outline: "none",
                                  fontSize: "14px",
                                }}
                                value={line}
                                onChange={(e) => {
                                  const newLines = [
                                    ...perfData.ai_generated_content
                                      .subject_lines,
                                  ];
                                  newLines[idx] = e.target.value;
                                  setPerfData({
                                    ...perfData,
                                    ai_generated_content: {
                                      ...perfData.ai_generated_content,
                                      subject_lines: newLines,
                                    },
                                  });
                                }}
                                onBlur={() => setEditingSubjectIdx(null)}
                              />
                            ) : (
                              <span
                                style={{
                                  color:
                                    isActive || isSelected
                                      ? "white"
                                      : "var(--text-dim)",
                                }}
                              >
                                {line}
                              </span>
                            )}

                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSubjectIdx(idx);
                              }}
                              style={{
                                position: "absolute",
                                right: "10px",
                                cursor: "pointer",
                                opacity: 0.7,
                              }}
                            >
                              ✏️
                            </span>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>

                {/* BODIES SECTION */}
                <div style={{ marginTop: "30px" }}>
                  <h4 style={{ marginBottom: "15px", color: "var(--accent)" }}>
                    Select & Edit Email Body
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                    }}
                  >
                    {perfData?.ai_generated_content?.email_bodies.map(
                      (body, idx) => {
                        const isSelected = perfData?.final_body?.html === body;
                        const isActive =
                          perfData?.selection_metadata?.selected_body_index ===
                          idx;

                        return (
                          <div
                            key={idx}
                            onClick={() =>
                              setPerfData({
                                ...perfData,
                                selection_metadata: {
                                  ...perfData.selection_metadata,
                                  selected_body_index: idx,
                                },
                              })
                            }
                            style={{
                              padding: "20px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: "2px solid",
                              position: "relative",
                              borderColor: isActive
                                ? "#f97316"
                                : isSelected
                                ? "var(--accent)"
                                : "var(--border)",
                              background: isActive
                                ? "rgba(249,115,22,0.15)"
                                : "var(--card)",
                            }}
                          >
                            {/* BADGES */}
                            {isSelected && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "40px",
                                  background: "var(--accent)",
                                  color: "black",
                                  fontSize: "10px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontWeight: "bold",
                                }}
                              >
                                ACTIVE
                              </span>
                            )}
                            {isActive && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "40px",
                                  background: "#f97316",
                                  color: "black",
                                  fontSize: "10px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontWeight: "bold",
                                }}
                              >
                                NEW SELECTED
                              </span>
                            )}

                            {/* EDITABLE TEXTAREA */}
                            {editingBodyIdx === idx ? (
                              <textarea
                                autoFocus
                                rows={6}
                                style={{
                                  width: "100%",
                                  background: "transparent",
                                  color: "white",
                                  border: "none",
                                  outline: "none",
                                  resize: "none",
                                  fontSize: "13px",
                                  lineHeight: "1.6",
                                }}
                                value={body}
                                onChange={(e) => {
                                  const newBodies = [
                                    ...perfData.ai_generated_content
                                      .email_bodies,
                                  ];
                                  newBodies[idx] = e.target.value;
                                  setPerfData({
                                    ...perfData,
                                    ai_generated_content: {
                                      ...perfData.ai_generated_content,
                                      email_bodies: newBodies,
                                    },
                                  });
                                }}
                                onBlur={() => setEditingBodyIdx(null)}
                              />
                            ) : (
                              <div
                                style={{
                                  whiteSpace: "pre-line",
                                  fontSize: "13px",
                                  color:
                                    isActive || isSelected
                                      ? "white"
                                      : "var(--text-dim)",
                                }}
                              >
                                {body}
                              </div>
                            )}

                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingBodyIdx(idx);
                              }}
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "15px",
                                cursor: "pointer",
                                opacity: 0.7,
                              }}
                            >
                              ✏️
                            </span>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>

              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <button
                  className="btn-primary"
                  style={{ width: "auto", padding: "12px 40px" }}
                  onClick={handleSaveEmailSelection}
                >
                  Save Selection
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* CSV MANAGEMENT TAB */}
      {/* CSV MANAGEMENT TAB */}
      {activeTab === "csv" && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            padding: "20px 0",
          }}
        >
          <div
            className="auth-card"
            style={{
              width: "100%",
              maxWidth: "500px",
              padding: "40px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "40px", marginBottom: "15px" }}>📁</div>
            <h3 style={{ marginTop: 0, marginBottom: "10px", color: "white" }}>
              Update Contact List
            </h3>
            <p
              style={{
                color: "var(--text-dim)",
                fontSize: "14px",
                marginBottom: "30px",
              }}
            >
              Upload a CSV to refresh your campaign recipients.
            </p>

            <div style={{ marginBottom: "30px" }}>
              {/* Hidden Real Input */}
              <input
                type="file"
                id="csv-upload"
                accept=".csv"
                onChange={(e) =>
                  setPerfData({ ...perfData, selectedFile: e.target.files[0] })
                }
                style={{ display: "none" }}
              />

              {/* Styled Proxy Button */}
              <label
                htmlFor="csv-upload"
                style={{
                  display: "inline-block",
                  padding: "12px 25px",
                  background: "rgba(59, 130, 246, 0.1)",
                  border: "1px dashed var(--accent)",
                  borderRadius: "8px",
                  color: "var(--accent)",
                  cursor: "pointer",
                  fontWeight: "bold",
                  transition: "0.3s",
                  width: "100%",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) =>
                  (e.target.style.background = "rgba(59, 130, 246, 0.2)")
                }
                onMouseOut={(e) =>
                  (e.target.style.background = "rgba(59, 130, 246, 0.1)")
                }
              >
                {perfData?.selectedFile ? "Change File" : "Choose CSV File"}
              </label>

              {/* File Name Display */}
              {perfData?.selectedFile && (
                <div
                  style={{
                    marginTop: "15px",
                    padding: "10px",
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: "6px",
                    fontSize: "13px",
                    color: "#4ade80",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <span>✅</span> {perfData.selectedFile.name}
                </div>
              )}
            </div>

            <button
              className="btn-primary"
              onClick={handleCSVUpload}
              disabled={!perfData?.selectedFile || loading}
              style={{
                width: "100%",
                opacity: !perfData?.selectedFile ? 0.5 : 1,
                cursor: !perfData?.selectedFile ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Processing..." : "Confirm & Upload"}
            </button>
          </div>
        </div>
      )}

      {/* SCHEDULE TAB */}
      {activeTab === "schedule" && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            padding: "20px 0",
          }}
        >
          <div
            className="auth-card"
            style={{
              width: "100%",
              maxWidth: "450px",
              padding: "40px",
              margin: "0",
            }}
          >
            <h2 style={{ textAlign: "center", marginTop: 0 }}>
              Campaign Schedule
            </h2>

            {/* CURRENT STATUS BOX - FIXED LOGIC */}
            <div
              style={{
                background: "rgba(59, 130, 246, 0.1)",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "25px",
                border: "1px solid rgba(59, 130, 246, 0.3)",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "11px",
                  color: "var(--accent)",
                  fontWeight: "bold",
                  letterSpacing: "1px",
                }}
              >
                CURRENT SYSTEM STATUS
              </p>
              <div style={{ marginTop: "10px" }}>
                <p style={{ margin: 0, fontSize: "15px", color: "white" }}>
                  Type:{" "}
                  <span
                    style={{ textTransform: "capitalize", fontWeight: "bold" }}
                  >
                    {perfData?.schedule_type || "Now"}
                  </span>
                </p>

                {/* ONLY show start_time if the type is NOT 'now' */}
                {perfData?.schedule_type !== "now" && perfData?.start_time && (
                  <p
                    style={{
                      margin: "8px 0 0 0",
                      fontSize: "13px",
                      color: "#4ade80",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <span>🕒</span> Scheduled for:{" "}
                    <strong>{perfData.start_time}</strong>
                  </p>
                )}
              </div>
            </div>

            <p
              style={{
                textAlign: "center",
                color: "var(--text-dim)",
                fontSize: "14px",
                marginBottom: "25px",
              }}
            >
              Change the launch timing for this campaign:
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  color: "var(--accent)",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                New Timing Mode
              </label>
              <select
                style={{ ...selectStyle, marginTop: "8px", cursor: "pointer" }}
                onChange={(e) =>
                  setPerfData({
                    ...perfData,
                    temp_schedule_type: e.target.value,
                  })
                }
                value={
                  perfData?.temp_schedule_type ||
                  perfData?.schedule_type ||
                  "now"
                }
              >
                <option value="now">Send Immediately</option>
                <option value="scheduled">Schedule Date/Time</option>
              </select>
            </div>

            {/* Date Input logic */}
            {(perfData?.temp_schedule_type === "scheduled" ||
              (!perfData?.temp_schedule_type &&
                perfData?.schedule_type === "scheduled")) && (
              <div style={{ marginTop: "20px" }}>
                <label
                  style={{
                    color: "var(--accent)",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  Set New Date & Time
                </label>
                <input
                  type="datetime-local"
                  step="1"
                  onChange={(e) => {
                    const val = e.target.value.replace("T", " ");
                    const formatted = val.length === 16 ? `${val}:00` : val;
                    setPerfData({ ...perfData, temp_schedule_date: formatted });
                  }}
                  style={{
                    ...selectStyle,
                    marginTop: "8px",
                    colorScheme: "dark",
                    padding: "12px",
                  }}
                />
              </div>
            )}

            <button
              className="btn-primary"
              style={{ marginTop: "30px", width: "100%" }}
              onClick={handleUpdateSchedule}
              disabled={loading}
            >
              {loading ? "Saving..." : "Confirm Schedule Change"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Internal Sub-component for Cards
const StatCard = ({ label, value, icon }) => (
  <div
    className="auth-card"
    style={{
      padding: "20px",
      display: "flex",
      alignItems: "center",
      gap: "20px",
      margin: "0", // CRITICAL: Remove any manual margins
      width: "auto", // Fills the flex lane
      height: "auto",
    }}
  >
    <div style={{ fontSize: "30px" }}>{icon}</div>
    <div>
      <p
        style={{
          margin: 0,
          fontSize: "12px",
          color: "var(--text-dim)",
          fontWeight: "bold",
        }}
      >
        {label.toUpperCase()}
      </p>
      <h2 style={{ margin: 0, fontSize: "24px" }}>{value}</h2>
    </div>
  </div>
);

// Styles
// const tableHeaderStyle = { padding: "15px 20px", fontSize: "13px" };
// const tableCellStyle = { padding: "15px 20px", fontSize: "14px" };

const miniSelectStyle = {
  width: "100%",
  padding: "12px",
  background: "var(--input)",
  border: "1px solid var(--border)",
  borderRadius: "8px",
  color: "white",
  marginTop: "8px",
  cursor: "pointer",
};

export default Tracker;
